import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { getRoundupNumber } from '../utils/roundup';

const post = (path, body) => axios.post(`${process.env.REACT_APP_WEB_URL}api/roundups/${path}`, body, { withCredentials: true });

export default function useRoundup() {
  const { user } = useUser();
  const [state, setState] = useState({ loading: true, year: null, number: null, roundup: null, items: [], goals: [], error: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [retry, setRetry] = useState(0);
  const draft = useRef({ items: [], goals: [] });
  const queue = useRef(Promise.resolve());

  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    const load = async () => {
      setState(previous => ({ ...previous, loading: true, error: '' }));
      try {
        const { data: years } = await axios.post(`${process.env.REACT_APP_WEB_URL}api/year/get-current-active-year`, {}, { withCredentials: true });
        if (!Array.isArray(years)) throw new Error('Unable to load your year.');
        const year = [...years].filter(item => new Date(item.startDate) <= new Date()).sort((a, b) => new Date(a.endDate) - new Date(b.endDate))[0];
        if (!year) {
          if (!cancelled) setState({ loading: false, year: null, number: null, roundup: null, items: [], goals: [], error: '' });
          return;
        }
        const number = getRoundupNumber(year.startDate);
        const key = { year_id: year._id, roundup_number: number };
        const { data } = await post('prepare/get-roundup-goals', key);
        if (!Array.isArray(data.fetchedRoundupGoals)) throw new Error('Unable to load this roundup.');
        const roundup = data.fetchedRoundupGoals[0] || null;
        let source = roundup;
        if (!source) {
          const fallback = await post('prepare/get-latest-available-roundup-goals', key);
          source = fallback.data.fetchedRoundupGoals?.[0];
        }
        const items = (source?.roundup_scorecard || []).map(item => ({ ...item, value: roundup ? item.value : 0 }));
        const goals = (source?.roundup_goals || []).filter(Boolean);
        if (!cancelled) {
          draft.current = { items, goals };
          setState({ loading: false, year, number, roundup, items, goals, error: '' });
        }
      } catch (error) {
        if (!cancelled) setState(previous => ({ ...previous, loading: false, error: error.response?.data?.message || 'Unable to load your roundup. Please try again.' }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.user_id, retry]);

  const key = { year_id: state.year?._id, roundup_number: state.number };
  const isCompleted = state.roundup?.status === 'completed';
  const persist = useCallback(() => {
    if (!state.year || !state.number || isCompleted) return Promise.reject(new Error('This roundup cannot be edited.'));
    // Queue complete drafts so a slow earlier save cannot overwrite a later edit.
    const payload = { year_id: state.year._id, roundup_number: state.number, items: draft.current.items, goals: draft.current.goals.map(goal => goal._id) };
    setSaving(true);
    const request = queue.current.catch(() => {}).then(async () => {
      const { data } = await post('prepare/update-scorecard-items', payload);
      if (!data.updateScorecardItems) throw new Error('Unable to save this roundup.');
      setState(previous => ({ ...previous, roundup: data.updateScorecardItems }));
      setSaveError('');
      return data.updateScorecardItems;
    });
    queue.current = request;
    request.catch(error => { setSaveError(error.response?.data?.message || 'Your changes could not be saved. Please try again.'); })
      .finally(() => { if (queue.current === request) setSaving(false); });
    return request;
  }, [state.year, state.number, isCompleted]);

  const changeItems = items => {
    if (isCompleted) return;
    draft.current = { ...draft.current, items };
    setState(previous => ({ ...previous, items }));
    return persist();
  };
  const changeGoals = goals => {
    if (isCompleted) return;
    draft.current = { ...draft.current, goals };
    setState(previous => ({ ...previous, goals }));
    return persist();
  };
  const updateGoal = goal => {
    const goals = draft.current.goals.map(item => item._id === goal._id ? goal : item);
    draft.current = { ...draft.current, goals };
    setState(previous => ({ ...previous, goals }));
  };
  const finish = async () => {
    await queue.current;
    const { data } = await post('complete-roundup', key);
    if (!data.completedRoundup) throw new Error('Unable to finish the roundup.');
    setState(previous => ({ ...previous, roundup: data.completedRoundup, goals: data.completedRoundup.roundup_summary?.goals || previous.goals }));
  };

  return { ...state, key, saving, saveError, isCompleted, changeItems, changeGoals, updateGoal, finish, persist, waitForSave: () => queue.current, retry: () => setRetry(value => value + 1) };
}
